import React, { useState, useContext } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  zIndexLayers,
  standardTransition,
  fontWeights,
  elementColours,
} from '../styles';
import { Container, Link } from './ui';
import MegaMenu from './MegaMenu';
import PhoneArtworkIconLinks from './PhoneArtworkIconLinks';
import { getAllProductCategoryItems } from '../utils';
import { StoreContext } from '../context/StoreContext';

const StyledNavigation = styled.nav`
  ${maxBreakpointQuery.large`
    position: fixed;
    top: 108px;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: ${zIndexLayers.third};
    display: ${({ isNavOpen }) => (isNavOpen ? 'block' : 'none')};
    padding-top: 20px;
    padding-bottom: 20px;
    background-color: ${standardColours.white};
  `}
`;

const StyledContainer = styled(Container)`
  ${maxBreakpointQuery.large`
    height: 100%;
    display: grid;
    align-content: space-between;
  `}
`;

const StyledList = styled.ul`
  ${minBreakpointQuery.large`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `}
`;

// moved up for hover
const StyledLink = styled(Link)`
  font-weight: ${fontWeights.semibold};
  transition: ${standardTransition('color')};

  ${maxBreakpointQuery.large`
    display: block;
    padding-top: 8px;
    padding-bottom: 8px;
  `}

  ${({ hasChildren }) => {
    if (hasChildren) {
      return css`
        position: relative;
        padding-right: 22px;

        ${minBreakpointQuery.large`
          padding-right: 16px;
        `}

        &:after {
          content: '';
          position: absolute;
          top: calc(50% + 1px);
          right: 2px;
          display: block;
          height: 9px;
          width: 9px;
          border: 2px solid ${elementColours.textColour};
          border-top: none;
          border-left: none;
          border-radius: 1px;
          transform: translateY(-50%) rotate(-45deg);
          transition: ${standardTransition('border-color')};

          ${minBreakpointQuery.large`
            height: 7px;
            width: 7px;
            border-width: 1px;
            transform: translateY(-75%) rotate(45deg);
          `}
        }
      `;
    }
  }}
`;

// moved up for hover
const StyledMegaMenu = styled(MegaMenu)`
  position: absolute;
  z-index: ${zIndexLayers.third};

  ${maxBreakpointQuery.large`
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: auto;
    transform: translateX(100%);
    transition: ${standardTransition('transform')};
  `}

  ${minBreakpointQuery.large`
    top: 100%;
    left: -9999em;
    width: 100%;
  `}

  ${({ isMegaMenuOpen }) => {
    if (isMegaMenuOpen) {
      return css`
        ${maxBreakpointQuery.large`
          transform: translateX(0);
        `}
      `;
    }
  }}
`;

const StyledItem = styled.li`
  ${maxBreakpointQuery.large`
    margin-top: 8px;
  `}

  ${minBreakpointQuery.large`
    display: inline-block;
    padding-top: 8px;
    padding-bottom: 12px;
  `}

  &:first-child {
    ${maxBreakpointQuery.large`
      margin-top: 0;
    `}

    ${minBreakpointQuery.large`
      margin-left: 0;
    `}
  }

  &:hover {
    ${StyledLink} {
      color: ${brandColours.secondary};

      ${({ hasChildren }) => {
        if (hasChildren) {
          return css`
            &:after {
              border-color: ${brandColours.secondary};
            }
          `;
        }
      }}
    }

    ${StyledMegaMenu} {
      ${minBreakpointQuery.large`
        left: 0;
      `}
    }
  }
`;

const Item = ({ items, node }) => {
  const { text, textOverride, ctas } = node;

  const hasChildren = items.length > 0;

  const [isMegaMenuOpen, setMegaMenuOpen] = useState();

  const { setOverlayActive } = useContext(StoreContext);

  return (
    <StyledItem
      {...(hasChildren && {
        hasChildren: true,
        onMouseOver: () => setOverlayActive(true),
        onMouseOut: () => setOverlayActive(false),
      })}
    >
      <StyledLink
        to={node}
        {...(hasChildren && {
          hasChildren: true,
          onClick: e => {
            if (
              matchMedia(`(max-width: ${breakpointSizes.large - 1}px)`).matches
            ) {
              e.preventDefault();
              setMegaMenuOpen(!isMegaMenuOpen);
            }
          },
        })}
        activeClassName="current-page"
      >
        {textOverride || text}
      </StyledLink>
      <StyledMegaMenu
        items={items}
        viewAllLink={node}
        ctas={ctas}
        isMegaMenuOpen={isMegaMenuOpen}
        setMegaMenuOpen={setMegaMenuOpen}
      />
    </StyledItem>
  );
};

const Navigation = ({ isNavOpen }) => {
  const {
    allDatoCmsProductCategory: { nodes },
    datoCmsHeader: { additionalNavigationItems },
  } = useStaticQuery(graphql`
    query NavigationQuery {
      allDatoCmsProductCategory(
        filter: { root: { eq: true }, removeFromNavigation: { ne: true } }
        sort: { position: ASC }
      ) {
        nodes {
          text: title
          textOverride: navigationText
          treeChildren {
            text: title
            textOverride: navigationText
            position
            removeFromNavigation
            ...LinkFragment
          }
          additionalItems {
            item {
              ... on DatoCmsPage {
                text: title
                ...LinkFragment
              }
              ... on DatoCmsProductCategory {
                text: title
                ...LinkFragment
              }
            }
            textOverride: navigationText
            removeFromNavigation
            positionOverride
          }
          ctas: navigationCtas {
            ...MegaMenuCtaFragment
          }
          removeFromNavigation
          ...LinkFragment
        }
      }
      datoCmsHeader {
        additionalNavigationItems {
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
    }
  `);

  const allItems = [
    ...nodes,
    ...additionalNavigationItems.map(({ pageUrl, text }) => ({
      ...pageUrl,
      text: text,
    })),
  ];

  return (
    <StyledNavigation isNavOpen={isNavOpen}>
      <StyledContainer>
        <StyledList>
          {allItems.map((node, id) => {
            const { treeChildren = [], additionalItems = [] } = node;

            const allItems = getAllProductCategoryItems(
              treeChildren,
              additionalItems
                .filter(({ item }) => item.slug)
                .map(
                  ({
                    item,
                    textOverride,
                    removeFromNavigation,
                    positionOverride,
                  }) => ({
                    ...item,
                    textOverride: textOverride,
                    removeFromNavigation: removeFromNavigation,
                    positionOverride: positionOverride,
                  }),
                ),
            ).filter(({ removeFromNavigation }) => !removeFromNavigation);

            return <Item key={id} items={allItems} node={node} />;
          })}
        </StyledList>
        <PhoneArtworkIconLinks isNav={true} />
      </StyledContainer>
    </StyledNavigation>
  );
};

export default Navigation;
